import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/LanguageContext";
import { api, API } from "../lib/api";
import { SignOut, Download, MagnifyingGlass, ArrowsClockwise } from "@phosphor-icons/react";

const STATUSES = ["New", "Reviewed", "Priority", "Invited", "Rejected"];

const statusColor = {
  New: "text-ivory border-ivory/30",
  Reviewed: "text-blue-300 border-blue-400/40",
  Priority: "text-brass border-brass/50",
  Invited: "text-green-300 border-green-400/40",
  Rejected: "text-red-300 border-red-400/40",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const a = t.admin;
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("newest");
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);
  const [resyncMsg, setResyncMsg] = useState("");

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subs, st] = await Promise.all([
        api.get("/admin/submissions", { params: { search, status, sort } }),
        api.get("/admin/stats"),
      ]);
      setRows(subs.data.submissions);
      setStats(st.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [search, status, sort, navigate]);

  useEffect(() => {
    if (user && user !== false) {
      const id = setTimeout(load, 250);
      return () => clearTimeout(id);
    }
  }, [user, load]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/submissions/${id}/status`, { status: newStatus });
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status: newStatus } : x)));
      const st = await api.get("/admin/stats");
      setStats(st.data);
    } catch (e) {
      /* ignore */
    }
  };

  const resyncAll = async () => {
    setResyncing(true);
    setResyncMsg("");
    try {
      const res = await api.post("/admin/resync-all");
      setResyncMsg(`${a.resyncDone}: ${res.data.synced}/${res.data.processed}`);
      await load();
    } catch (e) {
      setResyncMsg(e.response?.data?.detail || a.hubspotDisabled);
    } finally {
      setResyncing(false);
    }
  };

  const exportCsv = async () => {    const res = await fetch(`${API}/admin/export`, { credentials: "include" });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "habitus_waitlist.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (user === null) {
    return <div className="min-h-screen bg-dark-bg flex items-center justify-center font-mono text-ivory/40 text-xs uppercase tracking-widest">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-dark-bg text-ivory">
      {/* Top bar */}
      <header className="border-b border-dark-border">
        <div className="px-6 md:px-10 py-5 flex items-center justify-between">
          <div>
            <div className="font-mono text-sm tracking-[0.3em] uppercase">HABITUS</div>
            <div className="font-mono text-[0.65rem] uppercase tracking-widest text-ivory/40 mt-1">{a.dashboard}</div>
          </div>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-widest text-ivory/60 hover:text-brass transition-colors">
            <SignOut size={16} weight="thin" /> {a.logout}
          </button>
        </div>
      </header>

      <div className="px-6 md:px-10 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-dark-border border border-dark-border mb-8">
            <div data-testid="stat-total" className="bg-dark-surface p-5">
              <div className="font-mono text-[0.6rem] uppercase tracking-widest text-ivory/40">{a.total}</div>
              <div className="font-headings text-3xl mt-2">{stats.total}</div>
            </div>
            {STATUSES.map((s) => (
              <div key={s} className="bg-dark-surface p-5">
                <div className="font-mono text-[0.6rem] uppercase tracking-widest text-ivory/40">{s}</div>
                <div className="font-headings text-3xl mt-2">{stats.by_status[s] || 0}</div>
              </div>
            ))}
          </div>
        )}

        {stats && !stats.hubspot_enabled && (
          <div data-testid="hubspot-warning" className="border border-brass/40 text-brass px-5 py-3 mb-6 font-mono text-[0.65rem] uppercase tracking-widest">
            {a.hubspotDisabled}
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="flex items-center gap-2 border border-dark-border px-3 flex-1">
            <MagnifyingGlass size={16} weight="thin" className="text-ivory/40" />
            <input
              data-testid="admin-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={a.search}
              className="bg-transparent py-3 w-full font-mono text-xs focus:outline-none placeholder:text-ivory/30"
            />
          </div>
          <select data-testid="admin-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="border border-dark-border bg-dark-surface px-4 py-3 font-mono text-[0.7rem] uppercase tracking-widest focus:outline-none">
            <option value="all">{a.allStatuses}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="admin-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="border border-dark-border bg-dark-surface px-4 py-3 font-mono text-[0.7rem] uppercase tracking-widest focus:outline-none">
            <option value="newest">{a.sortNewest}</option>
            <option value="oldest">{a.sortOldest}</option>
          </select>
          <button data-testid="admin-export" onClick={exportCsv} className="flex items-center justify-center gap-2 border border-brass text-brass hover:bg-brass hover:text-dark-bg transition-colors px-5 py-3 font-mono text-[0.7rem] uppercase tracking-widest">
            <Download size={16} weight="thin" /> {a.export}
          </button>
          {stats?.hubspot_enabled && (
            <button data-testid="admin-resync-all" onClick={resyncAll} disabled={resyncing} className="flex items-center justify-center gap-2 border border-green-400/50 text-green-300 hover:bg-green-400 hover:text-dark-bg transition-colors px-5 py-3 font-mono text-[0.7rem] uppercase tracking-widest disabled:opacity-50">
              <ArrowsClockwise size={16} weight="thin" className={resyncing ? "animate-spin" : ""} /> {resyncing ? a.resyncing : a.resyncAll}
            </button>
          )}
        </div>

        {resyncMsg && (
          <div data-testid="admin-resync-msg" className="font-mono text-[0.65rem] uppercase tracking-widest text-green-300 mb-6">{resyncMsg}</div>
        )}

        {/* Table */}
        <div className="border border-dark-border overflow-x-auto">
          <table className="w-full font-mono text-xs">
            <thead>
              <tr className="border-b border-dark-border text-ivory/40 uppercase tracking-widest text-[0.6rem]">
                <th className="text-left p-4">{a.colName}</th>
                <th className="text-left p-4">{a.colContact}</th>
                <th className="text-left p-4">{a.colLocation}</th>
                <th className="text-left p-4">{a.colProfile}</th>
                <th className="text-left p-4">{a.colInterest}</th>
                <th className="text-left p-4">{a.colStatus}</th>
                <th className="text-left p-4">{a.colHubspot}</th>
                <th className="text-left p-4">{a.colDate}</th>
              </tr>
            </thead>
            <tbody data-testid="admin-table-body">
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center text-ivory/40">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-ivory/40" data-testid="admin-empty">{a.empty}</td></tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} data-testid={`admin-row-${r.id}`} className="border-b border-dark-border/60 hover:bg-dark-surface transition-colors align-top">
                    <td className="p-4">
                      <div className="text-ivory">{r.full_name}</div>
                      {r.note && <div className="text-ivory/40 mt-1 max-w-[200px] truncate" title={r.note}>{r.note}</div>}
                    </td>
                    <td className="p-4 text-ivory/70">
                      <div>{r.email}</div>
                      {r.phone && <div className="text-ivory/40 mt-1">{r.phone}</div>}
                    </td>
                    <td className="p-4 text-ivory/70">{[r.city, r.country].filter(Boolean).join(", ")}</td>
                    <td className="p-4 text-ivory/70">{r.profile_type}</td>
                    <td className="p-4 text-ivory/70">{r.interest_type}</td>
                    <td className="p-4">
                      <select
                        data-testid={`status-select-${r.id}`}
                        value={r.status}
                        onChange={(e) => updateStatus(r.id, e.target.value)}
                        className={`bg-transparent border px-2 py-1 uppercase tracking-widest text-[0.6rem] focus:outline-none ${statusColor[r.status] || "text-ivory border-ivory/30"}`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s} className="bg-dark-surface text-ivory">{s}</option>)}
                      </select>
                    </td>
                    <td className="p-4">
                      <span className={`text-[0.6rem] uppercase tracking-widest ${r.hubspot_status === "synced" ? "text-green-300" : r.hubspot_status === "error" ? "text-red-300" : "text-ivory/40"}`}>
                        {r.hubspot_status === "synced" ? a.hubspotSynced : r.hubspot_status === "error" ? a.hubspotError : a.hubspotPending}
                      </span>
                    </td>
                    <td className="p-4 text-ivory/40">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
